import { useState, useCallback } from "react";
import MfaAppService from "../services/MfaAppService"; // Adjust path if needed
import { useAppSelector } from "../store/hooks"; // Adjust path if needed

interface UseArchiveAppConfirmationProps {
  onSuccess?: () => void; // Callback after successful archive
}

export const useArchiveAppConfirmation = ({
  onSuccess,
}: UseArchiveAppConfirmationProps = {}) => {
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [itemToArchive, setItemToArchive] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const { user } = useAppSelector((state) => state.user); // Get user for API call

  const openArchiveConfirm = useCallback(
    (item: { id: string; name: string }) => {
      setItemToArchive(item);
      setArchiveError(null); // Clear previous errors
      setIsArchiveConfirmOpen(true);
    },
    [],
  );

  const closeArchiveConfirm = useCallback(() => {
    setIsArchiveConfirmOpen(false);
    setItemToArchive(null);
    setArchiveError(null);
  }, []);

  const confirmArchiveAction = useCallback(async () => {
    if (!itemToArchive || !user?.id) {
      setArchiveError("Cannot archive: Missing item ID or user information.");
      return;
    }

    setIsArchiving(true);
    setArchiveError(null);
    try {
      const mfaAppService = MfaAppService.getInstance();
      await mfaAppService.deleteProject(itemToArchive.id, user.id.toString());
      console.log(
        `Successfully archived app: ${itemToArchive.name} (ID: ${itemToArchive.id})`,
      );
      closeArchiveConfirm();
      onSuccess?.(); // Call the success callback (e.g., to refresh data)
    } catch (err) {
      console.error("Failed to archive app:", err);
      setArchiveError(
        err instanceof Error ? err.message : "Failed to archive app.",
      );
      // Keep the dialog open on error? Optionally close it: closeArchiveConfirm();
    } finally {
      setIsArchiving(false);
    }
  }, [itemToArchive, user?.id, closeArchiveConfirm, onSuccess]);

  return {
    isArchiveConfirmOpen,
    itemToArchive,
    isArchiving,
    archiveError,
    openArchiveConfirm,
    closeArchiveConfirm,
    confirmArchiveAction,
  };
};
